import Breadcrumb from "@/components/Breadcrumb";
import { getDetailBySlug } from "@/data/service-solution-details";
import RootNotFound from "@/app/not-found";
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

export default function SolutionDetailPage({ params }) {
  const { slug } = params;
  const locale = normalizeLocale(params?.locale);

  const item = getDetailBySlug("solutions", slug);

  if (!item) {
    return <RootNotFound />;
  }

  const pageUrl = sitePageUrl(locale, "solutions", slug);
  const homeUrl = sitePageUrl(locale);
  const metaTitle = `${item.title} | Highlandgroup`;
  const metaDescription = item.summary;
  const imageUrl = item.image?.startsWith("http")
    ? item.image
    : `${SITE_URL}${item.image}`;

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Value Offerings", href: "/solutions" },
    { label: item.title },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: metaTitle,
        description: metaDescription,
        inLanguage: locale,
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: imageUrl,
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          {
            "@type": "ListItem",
            position: 2,
            name: item.title,
            item: pageUrl,
          },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="Highlandgroup, digital solutions, Switzerland, business growth, value offerings"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={item.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={item.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <main className="min-h-screen bg-bg-main text-charcoal selection:bg-accent-red/20 selection:text-charcoal">
        <Breadcrumb
          items={breadcrumbItems}
          title={item.title}
          description={item.summary}
        />

        <section className="py-10 md:py-14 lg:py-16 border-t border-border-light bg-bg-main">
          <div className="container mx-auto px-5 sm:px-8 md:px-12 lg:px-16 max-w-5xl">
            <div className="mb-10 md:mb-12 rounded-2xl overflow-hidden shadow-lg bg-bg-soft">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-[260px] md:h-[340px] lg:h-[400px] object-cover"
              />
            </div>

            <div className="space-y-5 md:space-y-6 text-base md:text-lg leading-relaxed text-muted">
              {item.content.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
